// File: /src/pages/GridClients.js

import React, { useCallback } from 'react';
import GridClientsToolbar from '../components/Toolbar';
import ClientsGridView from '../components/clients/ClientsGridView';
import ClientsListView from '../components/clients/ClientsListView';
import ClientsTableView from '../components/clients/ClientsTableView';
import ClientModalView from '../components/ClientModalView';
import ClientForm from '../components/ClientForm';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useClientData } from '../hooks/useClientData';
import { useColorManager } from '../hooks/useColorManager';
import { useClientModalFunctions } from '../components/ClientModalFunctions';
import { useAuth } from '../hooks/useAuth';
import useClientSelection from '../hooks/clients/useClientSelection';
import useClientReports from '../hooks/clients/useClientReports';
import '../styles/GridClients.css';
import useProjectsData from '../hooks/useProjectsData';

function GridClients() {
const { user, loading, error } = useAuth();
const { setProjectsCache } = useProjectsData();

	const {
		clients,
		services,
		invoices,
		selectedClients,
		setSelectedClients,
		refreshClients,
		refreshProjects,
		refreshInvoices,
		toggleSelectClient,
	} = useClientData();

	const refreshProjectsWithCache = useCallback(async () => {
		const data = await refreshProjects();
		if (Array.isArray(data)) {
			setProjectsCache(data);
		}
	}, [refreshProjects, setProjectsCache]);

	// Gerenciamento de cores dos clientes
	const { cardColors, handleColorChange } = useColorManager(clients);

	// Funções para modal de clientes
	const modalFunctions = useClientModalFunctions(
		clients,
		refreshClients,
		refreshProjectsWithCache,
		refreshInvoices,
		services, // Passar dados em cache para evitar refetch
		invoices  // Passar dados em cache para evitar refetch
	);

	const selection = useClientSelection({
		clients,
		services,
		invoices,
		selectedClients,
		setSelectedClients,
		toggleSelectClient,
		modalFunctions,
		refreshClients,
		refreshProjectsWithCache,
		refreshInvoices,
	});

	const { handleGenerateReport } = useClientReports({
		clients,
		services,
		invoices,
	});

	const {
		viewMode,
		handleChangeView,
		showArchived,
		setShowArchived,
		filteredClients,
		filteredServices,
		filteredInvoices,
		selectedProjects,
		selectedInvoices,
		toggleSelectProject,
		toggleSelectInvoice,
		totalSelectedItems,
		handleArchiveSelected,
		handleUnarchiveSelected,
	} = selection;

	const handleReportRequest = (reportType) =>
		handleGenerateReport(reportType, {
			selectedClientsIds: selectedClients,
			selectedProjectIds: selectedProjects,
			selectedInvoiceIds: selectedInvoices,
		});

	if (loading)
		return (
			<p className="loading-text">
				Carregando...
				<br />
				<span className="loading-spinner"></span>
			</p>
		);

	if (error) {
		return <div>Erro ao carregar dados do usuário: {error.message}</div>;
	}

	return (
		<div className="grid-clients-page">
			<GridClientsToolbar
				viewMode={viewMode}
				onAddClient={modalFunctions.openNewClientModal}
				onGeneratePdf={() => handleReportRequest('pdf')}
				onGenerateXlsx={() => handleReportRequest('xlsx')}
				onChangeView={handleChangeView}
				selectedCount={totalSelectedItems}
				onArchiveSelected={handleArchiveSelected}
				onUnarchiveSelected={handleUnarchiveSelected}
				isShowingArchived={showArchived}
			/>

			<div className="grid-clients-h3">
				<h3>Gerenciamento de Clientes</h3>
			</div>

			{clients.length === 0 ? (
				<div className="no-clients-message">
					<p>
						Você ainda não possui nenhum cliente cadastrado. Clique
						no botão "Adicionar Cliente" para começar.
					</p>
				</div>
			) : (
				<>
					{viewMode === 'grid' && (
						<ClientsGridView
							clients={filteredClients}
							selectedClients={selectedClients}
							cardColors={cardColors}
							onToggleClient={toggleSelectClient}
							onColorChange={handleColorChange}
							onOpenClientModal={modalFunctions.openClientModal}
						/>
					)}

					{viewMode === 'list' && (
						<ClientsListView
							clients={filteredClients}
							selectedClients={selectedClients}
							cardColors={cardColors}
							onToggleClient={toggleSelectClient}
							onColorChange={handleColorChange}
							onOpenClientModal={modalFunctions.openClientModal}
						/>
					)}

					{viewMode === 'table' && (
						<ClientsTableView
							clients={filteredClients}
							services={filteredServices}
							invoices={filteredInvoices}
							cardColors={cardColors}
							showArchived={showArchived}
							onToggleArchived={() => setShowArchived((prev) => !prev)}
							selectedClients={selectedClients}
							toggleSelectClient={toggleSelectClient}
							selectedProjects={selectedProjects}
							toggleSelectProject={toggleSelectProject}
							selectedInvoices={selectedInvoices}
							toggleSelectInvoice={toggleSelectInvoice}
							onColorChange={handleColorChange}
							onOpenClientModal={modalFunctions.openClientModal}
						/>
					)}
				</>
			)}

			{modalFunctions.modalOpen &&
				(modalFunctions.selectedClient ? (
					<ClientModalView
						onClose={modalFunctions.closeClientModal}
						client={modalFunctions.selectedClient}
						projects={modalFunctions.projects}
						invoices={modalFunctions.invoices}
						tabIndex={modalFunctions.tabIndex}
						handleTabChange={modalFunctions.handleTabChange}
						modalFunctions={modalFunctions}
						user={user} // Agora user está definido
					/>
				) : (
					<div
						className="grid-modal-overlay"
						onClick={(e) => {
							if (
								e.target.classList.contains(
									'grid-modal-overlay'
								)
							) {
								modalFunctions.closeClientModal();
							}
						}}
					>
						<div className="grid-modal-content">
							<IconButton
								className="modal-close-btn"
								onClick={modalFunctions.closeClientModal}
							>
								<CloseIcon />
							</IconButton>
							<ClientForm
								form={modalFunctions.localClientForm}
								handleInputChange={
									modalFunctions.handleClientInputChange
								}
								addOrUpdateClient={
									modalFunctions.handleClientSubmit
								}
								editingClient={modalFunctions.selectedClient}
							/>
						</div>
					</div>
				))}
		</div>
	);
}

export default GridClients;
